import React, { useState } from 'react'
import { GatsbyImage, IGatsbyImageData } from 'gatsby-plugin-image'
import { PrismicRichText } from '@prismicio/react'
import { RichTextField } from '@prismicio/types'

import BertholdTitle from 'src/components/atoms/bertholdTitle'
import CustomLink from 'src/utils/customLink'
import { generateRandomId } from 'src/utils/domHelper'
import { PrismicLinkType } from 'src/typings/generated/graphql'
import OptionSelectModal, {
  OptionSelectModalContent,
} from 'src/components/organisms/ctas/optionSelectModal'
import * as GlobalStyles from 'src/styles/global.module.scss'
import * as Styles from './hoverCardGrid.module.scss'
import HoverCardGridModal from './hoverCardGridModal'

export enum HoverCardGridButtonVariant {
  link = 'link',
  modal = 'modal',
}

export type HoverCardGridCard = {
  title?: string
  description?: RichTextField
  image?: IGatsbyImageData
  imageAlt?: string
  modalContent?: RichTextField
  modalButtonText?: string
  modalButtonVariant?: HoverCardGridButtonVariant
  modalButtonLink?: PrismicLinkType
  modalButtonModal?: OptionSelectModalContent
}

export type HoverCardGridContent = {
  anchor?: string
  headline?: string
  description?: RichTextField
  buttonText?: string
  buttonVariant?: HoverCardGridButtonVariant
  buttonLink?: PrismicLinkType
  buttonModal?: OptionSelectModalContent
  cards: HoverCardGridCard[]
}

type Props = {
  content: HoverCardGridContent
}

const hasQuestionModalContent = (
  content?: OptionSelectModalContent
): content is OptionSelectModalContent =>
  Boolean(
    content?.title.trim() &&
      content.option1Label.trim() &&
      content.option2Label.trim() &&
      content.continueText.trim()
  )

const HoverCardGrid = ({ content }: Props) => {
  const {
    anchor = generateRandomId(),
    headline,
    description,
    buttonText,
    buttonVariant = HoverCardGridButtonVariant.link,
    buttonLink,
    buttonModal,
    cards,
  } = content

  const [activeCardIndex, setActiveCardIndex] = useState<number | null>(null)
  const [isCardModalClosing, setIsCardModalClosing] = useState(false)
  const [activeQuestionModal, setActiveQuestionModal] =
    useState<OptionSelectModalContent | null>(null)

  if (!cards || cards.length === 0) {
    return null
  }

  const sectionButtonOpensModal =
    buttonVariant === HoverCardGridButtonVariant.modal
  const sectionQuestionModal = hasQuestionModalContent(buttonModal)
    ? buttonModal
    : null
  const hasButton = Boolean(
    buttonText?.trim() &&
      (!sectionButtonOpensModal || sectionQuestionModal !== null)
  )

  const activeCard =
    activeCardIndex !== null ? cards[activeCardIndex] ?? null : null
  const activeCardQuestionModal = hasQuestionModalContent(
    activeCard?.modalButtonModal
  )
    ? activeCard?.modalButtonModal
    : null

  const closeCardModal = () => {
    setActiveCardIndex(null)
    setIsCardModalClosing(false)
  }

  const openCardQuestionModal = () => {
    if (activeCardQuestionModal) setIsCardModalClosing(true)
  }

  const finishCardModalTransition = () => {
    const nextModal = activeCardQuestionModal
    closeCardModal()
    if (nextModal) setActiveQuestionModal(nextModal)
  }

  return (
    <section id={anchor} className={Styles.hoverCardGrid}>
      <div className={Styles.inner}>
        {headline && (
          <h2 className={Styles.headline}>
            {BertholdTitle({ text: headline })}
          </h2>
        )}
        {description && (
          <div className={Styles.description}>
            <PrismicRichText
              field={description}
              components={{
                hyperlink: ({ node, children }: any) =>
                  CustomLink({ link: node.data, children }),
              }}
            />
          </div>
        )}
        {hasButton && (
          <div className={Styles.buttonWrapper}>
            {sectionButtonOpensModal ? (
              <button
                type="button"
                className={`${GlobalStyles.redButton} ${Styles.button}`}
                onClick={() => {
                  if (sectionQuestionModal) {
                    setActiveQuestionModal(sectionQuestionModal)
                  }
                }}
              >
                {buttonText}
              </button>
            ) : (
              <CustomLink
                link={buttonLink}
                className={`${GlobalStyles.redButton} ${Styles.button}`}
              >
                {buttonText}
              </CustomLink>
            )}
          </div>
        )}
        <div className={Styles.grid}>
          {cards.map((card, i) => {
            const key = `hover-card-${i}`
            return (
              <button
                key={key}
                type="button"
                className={Styles.card}
                onClick={() => {
                  setActiveCardIndex(i)
                  setIsCardModalClosing(false)
                }}
                aria-label={card.title ?? undefined}
              >
                {card.image && (
                  <div className={Styles.imageWrapper}>
                    <GatsbyImage
                      className={Styles.image}
                      image={card.image}
                      alt={card.imageAlt ?? ''}
                    />
                  </div>
                )}
                {card.title && (
                  <h3 className={Styles.cardTitle}>{card.title}</h3>
                )}
                {card.description && (
                  <div className={Styles.cardDescription}>
                    <PrismicRichText
                      field={card.description}
                      components={{
                        hyperlink: ({ node, children }: any) =>
                          CustomLink({ link: node.data, children }),
                      }}
                    />
                  </div>
                )}
              </button>
            )
          })}
        </div>
      </div>
      <HoverCardGridModal
        card={activeCard}
        isOpen={activeCardIndex !== null}
        isClosing={isCardModalClosing}
        onClose={closeCardModal}
        onModalButtonClick={
          activeCardQuestionModal ? openCardQuestionModal : undefined
        }
        onExited={finishCardModalTransition}
      />
      {activeQuestionModal && (
        <OptionSelectModal
          content={activeQuestionModal}
          isOpen
          onClose={() => setActiveQuestionModal(null)}
        />
      )}
    </section>
  )
}

export default HoverCardGrid
